import React, { useContext, useState } from "react";
import "./Login.css";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import classNames from "classnames";
import { LoginPost } from "../../utils/apis/Login";
import { Context } from "../../utils/context";
import Cookies from "js-cookie";
import { Navigate, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";
import { CookieName } from "../../utils/common";

// Cards fanned out on the table (a pure sequence + joker)
const HAND = [
  { rank: "7", suit: "♥" },
  { rank: "8", suit: "♥" },
  { rank: "9", suit: "♥" },
  { rank: "10", suit: "♥" },
  { rank: "J", suit: "♥" },
  { rank: "Q", suit: "♥" },
  { rank: "K", suit: "♥" },
  { rank: "A", suit: "♠" },
  { rank: "A", suit: "♣" },
  { rank: "A", suit: "♦" },
  { rank: "JOKER", suit: "★" },
];

// Cards floating in the background
const FLOATING = [
  { rank: "A", suit: "♠", className: "float-1" },
  { rank: "K", suit: "♦", className: "float-2" },
  { rank: "Q", suit: "♣", className: "float-3" },
  { rank: "J", suit: "♥", className: "float-4" },
];

const isRed = (suit) => suit === "♥" || suit === "♦";

function PlayingCard({ rank, suit, className = "", style }) {
  const joker = rank === "JOKER";
  return (
    <div
      className={classNames("playing-card", className, {
        red: isRed(suit),
        joker,
      })}
      style={style}>
      <span className="corner top">
        <b>{joker ? "J" : rank}</b>
        <i>{suit}</i>
      </span>
      <span className="center-suit">{joker ? "🃏" : suit}</span>
      <span className="corner bottom">
        <b>{joker ? "J" : rank}</b>
        <i>{suit}</i>
      </span>
    </div>
  );
}

function Login() {
  const { signin, setSignin, setUsertype, setisAllow } = useContext(Context);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm();

  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      const result = await LoginPost(data);

      if (result?.data?.success) {
        setSignin(result?.data?.success);
        setUsertype(result?.data?.data?.role);
        setisAllow(result?.data?.data?.permissions);

        Cookies.set(CookieName, result?.data?.data?.token, {
          expires: 365,
        });

        navigate("/dashboard");
      } else {
        setError("password", {
          message:
            typeof result?.errors === "string"
              ? result.errors
              : result?.data?.message || "Login failed",
        });
      }
      return;
    } catch (error) {
      console.error("Login Error:", error);

      if (error.response) {
        const errors = error.response.data?.errors;

        if (errors) {
          Object.keys(errors).forEach((field) => {
            const fieldError = errors[field];
            setError(field, {
              type: "server",
              message:
                typeof fieldError === "string"
                  ? fieldError
                  : fieldError?.msg || fieldError?.message || "Invalid value",
            });
          });
        } else {
          setError("password", {
            type: "server",
            message: error.response.data?.message || "Something went wrong.",
          });
        }
      } else {
        setError("password", {
          type: "server",
          message: error.message,
        });
      }
    }
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  if (signin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="Login">
      {/* Background floating cards & spotlight */}
      <div className="spotlight" />
      {FLOATING.map((card) => (
        <PlayingCard
          key={card.className}
          {...card}
          className={`floating-card ${card.className}`}
        />
      ))}

      <div className="login-stage">
        {/* Rummy table */}
        <div className="table-side">
          <div className="rummy-table">
            <div className="table-felt">
              <div className="table-brand">RUMMY SAMRAT</div>

              <div className="deck-area">
                <div className="card-back stack" />
                <PlayingCard rank="5" suit="♣" className="discard" />
              </div>

              <div className="hand">
                {HAND.map((card, i) => (
                  <PlayingCard
                    key={i}
                    {...card}
                    className="hand-card"
                    style={{
                      "--i": i - (HAND.length - 1) / 2,
                      animationDelay: `${i * 0.08}s`,
                    }}
                  />
                ))}
              </div>

              <div className="chips">
                <span className="chip chip-red" />
                <span className="chip chip-blue" />
                <span className="chip chip-gold" />
                <span className="chip chip-black" />
              </div>
            </div>
          </div>
          <p className="tagline">
            Deal the cards. <span>Rule the table.</span>
          </p>
        </div>

        {/* Login panel */}
        <div className="form-side">
          <div className="main-Form-holder">
            <div className="suits-row">
              <span>♠</span>
              <span className="red">♥</span>
              <span>♣</span>
              <span className="red">♦</span>
            </div>

            <div className="img-holder">
              <img
                className="logo-img"
                src={process.env.PUBLIC_URL + "/logo.png"}
                alt="logo-img"
              />
            </div>

            <h2 className="welcome-title">Admin Login</h2>
            <p className="welcome-sub">Take your seat at the table</p>

            <div className="form-holder">
              <Form onSubmit={handleSubmit(onSubmit)} role="form" noValidate>
                <Form.Group className="mb-3" controlId="formBasicEmail">
                  <Form.Label className="font-use">Email</Form.Label>
                  <div className="input-wrap">
                    <FaEnvelope className="input-icon" />
                    <Form.Control
                      type="email"
                      placeholder="Enter Email"
                      className={classNames({
                        "is-invalid": errors?.email,
                      })}
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Please enter a valid email address",
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <span className="text-danger">
                      {errors.email.message}
                    </span>
                  )}
                </Form.Group>

                <Form.Group className="mb-3" controlId="formBasicPassword">
                  <Form.Label className="font-use">Password</Form.Label>
                  <div className="input-wrap">
                    <FaLock className="input-icon" />
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      className={classNames({
                        "is-invalid": errors?.password,
                        "no-error-icon": errors?.password,
                      })}
                      {...register("password", {
                        required: "Password is required",
                      })}
                    />
                    <span
                      className="password-toggle"
                      onClick={togglePasswordVisibility}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </span>
                  </div>
                  {errors.password && (
                    <span className="text-danger">
                      {errors.password.message}
                    </span>
                  )}
                </Form.Group>

                <Button
                  className="sign-up-btn font-use"
                  type="submit"
                  disabled={isSubmitting}>
                  {isSubmitting ? "Dealing..." : "Login"}
                  <FontAwesomeIcon icon={faCircleArrowRight} />
                </Button>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;
